import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const SESSION_STATUS = {
  pending: "bg-[#E2E0DD] text-[#5C5955]",
  scheduled: "bg-[#F9E8C0] text-[#7A5C1F]",
  completed: "bg-[#D4E4D3] text-[#2F4F2F]",
  cancelled: "bg-[#F2D6D3] text-[#8C3A3A]",
};
const JOURNEY_STATUS = ["active", "completed", "paused", "cancelled"];

function SessionRow({ journeyId, session, onSaved }) {
  const [s, setS] = useState(session);
  const [saving, setSaving] = useState(false);
  useEffect(() => setS(session), [session]);
  const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/journeys/${journeyId}/sessions/${s.session_number}`, {
        status: s.status, scheduled_at: s.scheduled_at || "", zoom_link: s.zoom_link || "", notes: s.notes || "",
      });
      onSaved(data);
      toast.success(`Session ${s.session_number} saved`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-[#EBE5D9] p-4 space-y-3" data-testid={`session-${s.session_number}`}>
      <div className="flex items-center justify-between">
        <div className="font-serif text-lg">Session {s.session_number}</div>
        <span className={`badge-soft ${SESSION_STATUS[s.status]}`}>{s.status}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs uppercase text-[#7E736E]">Status</Label>
          <Select value={s.status} onValueChange={(v) => upd("status", v)}>
            <SelectTrigger data-testid={`session-${s.session_number}-status`}><SelectValue /></SelectTrigger>
            <SelectContent>{Object.keys(SESSION_STATUS).map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-[#7E736E]">Date &amp; time</Label>
          <Input type="datetime-local" value={(s.scheduled_at || "").slice(0, 16)} onChange={(e) => upd("scheduled_at", e.target.value)} data-testid={`session-${s.session_number}-datetime`} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs uppercase text-[#7E736E]">Zoom link</Label>
          <Input value={s.zoom_link || ""} placeholder="https://zoom.us/j/…" onChange={(e) => upd("zoom_link", e.target.value)} data-testid={`session-${s.session_number}-zoom`} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs uppercase text-[#7E736E]">Notes</Label>
          <Textarea rows={2} value={s.notes || ""} onChange={(e) => upd("notes", e.target.value)} data-testid={`session-${s.session_number}-notes`} />
        </div>
      </div>
      <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid={`session-${s.session_number}-save`}>
        {saving ? "Saving…" : "Save session"}
      </button>
    </div>
  );
}

export default function JourneysPage() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ status: "", q: "" });
  const [open, setOpen] = useState(false);
  const [journey, setJourney] = useState(null);

  const load = async () => {
    const params = {}; Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    try { const { data } = await api.get("/admin/journeys", { params }); setItems(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [f]);

  const openJourney = async (j) => {
    try { const { data } = await api.get(`/admin/journeys/${j.id}`); setJourney(data); setOpen(true); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const saveHeader = async () => {
    try {
      const { data } = await api.patch(`/admin/journeys/${journey.id}`, {
        status: journey.status, coach_name: journey.coach_name, notes: journey.notes,
      });
      setJourney(data); toast.success("Journey updated"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const onSessionSaved = (data) => { setJourney(data); load(); };

  const del = async () => {
    if (!window.confirm("Delete this journey? This cannot be undone.")) return;
    await api.delete(`/admin/journeys/${journey.id}`); setOpen(false); load();
  };

  return (
    <div className="space-y-6 fade-up" data-testid="admin-journeys">
      <div>
        <h1 className="font-serif text-4xl">iC One-to-One Journeys</h1>
        <p className="text-sm text-[#7E736E]">Track each participant through their 7-session one-to-one journey. Journeys are created by converting a completed Clarity Call.</p>
      </div>

      <div className="soft-card !p-5 grid sm:grid-cols-3 gap-3">
        <Input placeholder="Search name / email" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="journey-search" />
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="journey-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{JOURNEY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={() => setF({ status: "", q: "" })} className="btn-ghost text-sm">Clear</button>
      </div>

      <div className="soft-card !p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="journeys-table">
          <thead className="bg-[#F3EFEA] text-xs uppercase tracking-wider text-[#7E736E]">
            <tr><th className="text-left p-4">Participant</th><th className="text-left">Coach</th><th className="text-left">Progress</th><th className="text-left">Status</th><th className="text-left">Started</th></tr>
          </thead>
          <tbody>
            {items.map((j) => (
              <tr key={j.id} onClick={() => openJourney(j)} className="border-t border-[#EBE5D9] cursor-pointer hover:bg-[#FDFBF7]" data-testid={`journey-row-${j.id}`}>
                <td className="p-4"><div className="font-medium">{j.participant_name}</div><div className="text-xs text-[#7E736E]">{j.participant_email}</div></td>
                <td className="text-xs">{j.coach_name}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-28 h-2 rounded-full bg-[#EBE5D9] overflow-hidden">
                      <div className="h-full bg-terracotta" style={{ width: `${(j.completed_count / j.total_sessions) * 100}%` }} />
                    </div>
                    <span className="text-xs text-[#7E736E]">{j.completed_count}/{j.total_sessions}</span>
                  </div>
                </td>
                <td><span className="badge-soft bg-[#F9E8C0] text-[#7A5C1F]">{j.status}</span></td>
                <td className="text-xs text-[#7E736E]">{(j.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#7E736E]">No journeys yet. Convert a completed Clarity Call from the Bookings page.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setJourney(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="journey-detail">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{journey?.participant_name} · Journey</DialogTitle></DialogHeader>
          {journey && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-[#7E736E]">Journey status</Label>
                  <Select value={journey.status} onValueChange={(v) => setJourney({ ...journey, status: v })}>
                    <SelectTrigger data-testid="journey-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{JOURNEY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-[#7E736E]">Coach</Label>
                  <Input value={journey.coach_name || ""} onChange={(e) => setJourney({ ...journey, coach_name: e.target.value })} data-testid="journey-coach" />
                </div>
                <div className="space-y-1 flex items-end">
                  <button onClick={saveHeader} className="btn-primary text-sm w-full" data-testid="journey-save-header">Save details</button>
                </div>
                <div className="space-y-1 sm:col-span-3">
                  <Label className="text-xs uppercase text-[#7E736E]">Journey notes</Label>
                  <Textarea rows={2} value={journey.notes || ""} onChange={(e) => setJourney({ ...journey, notes: e.target.value })} data-testid="journey-notes" />
                </div>
              </div>
              <div className="text-xs text-[#7E736E]">Progress: {journey.completed_count}/{journey.total_sessions} sessions completed</div>
              <div className="space-y-3">
                {journey.sessions.map((s) => (
                  <SessionRow key={s.session_number} journeyId={journey.id} session={s} onSaved={onSessionSaved} />
                ))}
              </div>
              <button onClick={del} className="text-xs text-red-700 underline" data-testid="journey-delete">Delete journey</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
